import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EnvironmentInjector,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  runInInjectionContext,
  Signal,
  signal,
  untracked,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MediaWatcherService } from '@iot-platform/core';
import { IotPlatformDashboardsModule } from '@iot-platform/feature/dashboards';
import { IotPlatformMapsModule } from '@iot-platform/feature/maps';
import { GridEngineFacade, GridEngineModule, GridScrollStateService } from '@iot-platform/grid-engine';
import {
  FilterEngineV2Component,
  IotToolbarDefaultButton,
  IotToolbarDispatchActionType,
  IotToolbarMenuButton,
  IotToolbarMenuButtonOption,
  ResizeHandleComponent,
  SectionHeaderComponent,
  SectionHeaderEvent,
  ToolbarPageType,
  ToolbarV2Module
} from '@iot-platform/iot-platform-ui';
import {
  CommonIndexedPagination,
  FavoriteView,
  Filter,
  IotToolbarEvent,
  MasterViewEngineEvent,
  Pagination,
  PlatformResponse,
  ToolbarSize,
  UserAccount
} from '@iot-platform/models/common';
import { Dashboard } from '@iot-platform/models/dashboards';
import { I4BGrid, I4BGridData, I4BGridOptions, I4BGridSort } from '@iot-platform/models/grid-engine';
import { IotMapDisplayMode } from '@iot-platform/models/maps';
import { TranslatePipe } from '@ngx-translate/core';
import { get, isEqual } from 'lodash';
import { filter, lastValueFrom, map, Observable, of, tap, withLatestFrom } from 'rxjs';
import { AbstractMasterViewComponent } from '../../components/abstract-master-view.component';
import { FeatureMasterViewDrawerDetailsLayout, FeatureMasterViewSettings } from '../../models/master-view-settings.model';
import { AbstractMasterViewDashboardService } from '../../services/abstract-master-view-dashboard.service';
import { AbstractMasterViewMapService } from '../../services/abstract-master-view-map.service';
import { AbstractMasterViewService } from '../../services/abstract-master-view.service';
import { getMasterViewInjectionTokens } from '../../settings/feature-master-view-settings.provider';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'iot-platform-feature-master-view',
  imports: [
    GridEngineModule,
    ToolbarV2Module,
    TranslatePipe,
    FlexLayoutModule,
    MatSidenavModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatToolbarModule,
    NgComponentOutlet,
    SectionHeaderComponent,
    IotPlatformDashboardsModule,
    IotPlatformMapsModule,
    NgTemplateOutlet,
    FilterEngineV2Component,
    ResizeHandleComponent
  ],
  templateUrl: './master-view.component.html',
  styleUrl: './master-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeatureMasterViewComponent<T> extends AbstractMasterViewComponent<T> implements OnInit, OnDestroy {
  readonly FeatureMasterViewDrawerDetailsLayout = FeatureMasterViewDrawerDetailsLayout;
  settings: FeatureMasterViewSettings;
  @Input() masterView: string;
  @Output() gridReady: EventEmitter<Signal<I4BGrid<I4BGridOptions, I4BGridData>>> = new EventEmitter<Signal<I4BGrid<I4BGridOptions, I4BGridData>>>();
  @ViewChild('sidenav', { static: false }) sidenav?: MatSidenav;
  selectedRowId: WritableSignal<string> = signal(null);
  canUpdateBusinessProfile: Signal<boolean>;
  grid: WritableSignal<I4BGrid<I4BGridOptions, I4BGridData>> = signal(null);
  staticGrid: Signal<I4BGrid<I4BGridOptions, I4BGridData>>;
  toolbarSize: WritableSignal<string> = signal(ToolbarSize.SMALL);
  permissions: Signal<
    {
      key: string;
      value: boolean;
    }[]
  > = signal([]);
  userPermissions: Signal<
    {
      key: string;
      value: boolean;
    }[]
  > = computed(() => {
    this.authFacade.privileges();
    return this.permissions();
  });
  gridsConfiguration: Signal<{
    sortedGridsWithoutAppDefault: I4BGrid<I4BGridOptions, I4BGridData>[];
    currentGrid: I4BGrid<I4BGridOptions, I4BGridData> | undefined;
    isGridsLoading: boolean;
  }> = signal(null);
  grids: Signal<I4BGrid<I4BGridOptions, I4BGridData>[]>;
  currentUser: Signal<UserAccount>;
  favoriteViewsConfiguration: Signal<{
    sortedFavoriteViews: FavoriteView[];
    currentFavoriteView: FavoriteView | undefined;
    isFavoriteViewsLoading: boolean;
  }>;
  timer: WritableSignal<number> = signal(0);
  // Dashboards
  dashboardsConfiguration: Signal<{
    sortedDashboards: Dashboard[];
    currentDashboard: Dashboard | undefined;
    isDashboardsLoading: boolean;
  }> = signal(null);
  // Map
  mapDisplayMode: Signal<IotMapDisplayMode> = signal(null);
  mapConcept: Signal<string> = signal(null);
  // Toolbar
  pageType: WritableSignal<ToolbarPageType> = signal(ToolbarPageType.GRID);
  toolbarButtons: Signal<(IotToolbarDefaultButton | IotToolbarMenuButton)[]>;
  ToolbarPageType = ToolbarPageType;
  selectedGrid: Signal<I4BGrid<I4BGridOptions, I4BGridData>>;
  selectedGridId: Signal<string> = computed(() => {
    const selectedGrid = this.selectedGrid();
    return get(selectedGrid, ['id'], 'default');
  });
  dataLoadedByGridSelector: Signal<boolean>;
  dataLoadingByGridSelector: Signal<boolean>;
  gridSort: WritableSignal<I4BGridSort[]> = signal([]);

  sideNavContentWidth: WritableSignal<string> = signal('auto');
  drawerWidth: WritableSignal<string> = signal('25rem');

  private masterViewService: AbstractMasterViewService;
  private masterViewDashboardService: AbstractMasterViewDashboardService;
  private masterViewMapService: AbstractMasterViewMapService;
  private readonly gridEngineFacade: GridEngineFacade = inject(GridEngineFacade);
  gridSortByGridId$: Observable<I4BGridSort[]> = toObservable(this.gridEngineFacade.gridSortByGridId);
  private readonly gridScrollStateService: GridScrollStateService = inject(GridScrollStateService);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly mediaWatcherService = inject(MediaWatcherService);
  readonly isXSmallScreen = this.mediaWatcherService.isXSmallScreen;
  private get masterViewEvent() {
    return {
      event: null,
      currentFilters: this.currentFilters(),
      filterEngineOpened: this.filterEngineOpened(),
      grid: this.grid(),
      grids: this.grids(),
      currentFavoriteView: this.currentFavoriteView()
    };
  }

  getRequest(page: number) {
    const grid = this.grid();
    if (!grid) {
      return {
        ...super.getRequest(page),
        additionalOptions: this.settings?.additionalOptions?.()
      };
    }
    return {
      ...super.getRequest(page),
      concept: grid?.masterview?.toLowerCase(),
      variables: grid?.gridOptions?.variableNames,
      tags: grid?.gridOptions?.tagIds,
      endPoint: grid?.gridOptions?.endPoint,
      additionalOptions: this.settings?.additionalOptions?.(),
      gridOptions: grid?.gridOptions
    };
  }

  // Enable inherit business profile change event only for static grid definition
  handleBusinessProfileChange() {
    if (this.settings?.grid?.staticDefinitionType()) {
      super.handleBusinessProfileChange();
    }
  }

  onTimerValueChanged(value: number) {
    this.timer.set(value);
  }

  ngOnInit() {
    // These two effects need to be initialized before super.ngOnInit()
    // otherwise we call reloadGrid with the default grid and not the selected one
    this.initInjectionContext();
    this.initSelectedGridByMasterview();
    this.initCustomGridOptions();
    //
    super.ngOnInit();
    this.initGridEffect();
    this.initDefaultGridEffect();
    this.initFilterOpenedEffect();
    this.initFavoriteViewButtonsEffect();
    this.initGridPaginationEffect();
    this.onBusinessProfileChange();
    this.handleBusinessProfileChange();
    this.initGridDataEffect();
    if (!this.settings.grid.staticDefinitionType()) {
      this.initLoadedEffect();
      this.initTotalEffect();
    }
    this.gridReady.emit(this.grid);
  }

  onPageChange(pagination: Pagination) {
    if (this.settings.grid.staticDefinitionType()) {
      super.onPageChange(pagination);
    }
    this.toggleDrawerDetails(false);
  }

  onToolbarEvent(event: IotToolbarEvent): void {
    super.onToolbarEvent(event);
    switch (event.type) {
      case IotToolbarDispatchActionType.TOGGLE_PAGE_TYPE.toString(): {
        this.pageType.set(event.options.pageType);
        this.gridScrollStateService.clearAll();
        break;
      }
      case IotToolbarDispatchActionType.APPLY_GRID.toString():
        this.onApplyGrid(event.options.grid);
        break;
      case IotToolbarDispatchActionType.APPLY_DASHBOARD.toString():
        this.masterViewDashboardService.onToolbarEvent({
          ...this.masterViewEvent,
          event
        });
        break;
      case IotToolbarDispatchActionType.REFRESH_PAGE.toString(): {
        if (this.masterViewMapService instanceof AbstractMasterViewMapService) {
          this.masterViewMapService.onToolbarEvent({
            ...this.masterViewEvent,
            event
          });
        }
        this.gridScrollStateService.clearAll();
        break;
      }
      case this.settings.drawerDetails?.customOpeningEventType?.()?.toString():
        this.toggleDrawerDetails(!this.settings.drawerDetails.opened());
        break;
    }

    this.masterViewService.onToolbarEvent({
      ...this.masterViewEvent,
      event
    });
  }

  onMasterViewEngineEvent(event: MasterViewEngineEvent | SectionHeaderEvent): void {
    if (this.settings.drawerDetails.enabled()) {
      if (
        (!this.settings.drawerDetails.customOpeningEventType() && event.type === 'rowClicked') ||
        (this.settings.drawerDetails.customOpeningEventType() && this.settings.drawerDetails.customOpeningEventType() === event.type)
      ) {
        if ((event as MasterViewEngineEvent)?.node?.group && this.settings.drawerDetails.suppressRowGroupClickEvent()) {
          return;
        }
        this.toggleDrawerDetails(true);
      }
    }

    this.masterViewService.onMasterViewEngineEvent({
      ...this.masterViewEvent,
      event
    });
  }

  onDispatchGridEvent({ event }): void {
    this.onMasterViewEngineEvent(event);
  }

  onMapEvent(event): void {
    this.masterViewMapService.onMapEvent(event);
  }

  onMapNavigationEvent(event): void {
    this.masterViewMapService.onDispatchMapNavigationEvent(event);
  }

  onApplyFilters(currentFilters: Filter[]): void {
    super.onApplyFilters(currentFilters);
    this.gridScrollStateService.clearAll();
    this.masterViewService.onMasterViewEngineEvent({
      ...this.masterViewEvent,
      event: { type: 'applyFilters', options: { filters: currentFilters } }
    });
  }

  onApplyGrid(grid: I4BGrid<I4BGridOptions, I4BGridData>): void {
    this.selectGridAndLoadData(grid.id, grid.masterview, this.currentFilters());
  }

  toggleDrawerDetails(open: boolean) {
    (this.settings?.drawerDetails?.opened as WritableSignal<boolean>)?.set?.(open);
  }

  onBackdropClick(): void {
    if (this.settings.drawerDetails.closeOnBackdropClick?.() && this.sidenav) {
      this.toggleDrawerDetails(false);
      this.sidenav.close();
    }
  }

  reLoadMasterView() {
    const grid = this.grid();
    const currentFilters = this.currentFilters();

    if (this.settings.grid.staticDefinitionType()) {
      super.reLoadMasterView();
    } else if (grid?.data) {
      this.pagination.set({
        ...grid.data.response.pagination,
        currentPage: 0 // Force to start from the first page
      } as CommonIndexedPagination);
      const request = this.getRequest(0);
      this.facade.setFilters(request.filters);
      this.gridEngineFacade.reloadGridData(request);
    } else {
      const masterViewName = this.settings?.masterViewName();
      this.selectGridAndLoadData(this.selectedGridId(), masterViewName, currentFilters);
    }
    this.toggleDrawerDetails(false);
  }

  sortData(platformResponse: PlatformResponse): PlatformResponse {
    return platformResponse;
  }

  ngOnDestroy() {
    if (this.settings?.filterEngine?.clearOnDestroy?.()) {
      this.facade.setFilters([]);
    }
    if (!this.settings?.drawerDetails?.openedOnDestroy?.()) {
      this.toggleDrawerDetails(false);
    }
  }

  private initInjectionContext(): void {
    runInInjectionContext(this.environmentInjector, () => {
      const { authFacade, masterViewService, masterViewFacade, settings, masterViewDashboardService, masterViewMapService } = getMasterViewInjectionTokens(
        this.masterView
      );
      this.authFacade = authFacade;
      this.facade = masterViewFacade;
      this.settings = settings;
      this.gridEngineFacade.selectCurrentMaterView(null);
      this.masterViewService = masterViewService;
      this.masterViewDashboardService = masterViewDashboardService;
      this.masterViewMapService = masterViewMapService;
      this.currentUser = this.authFacade.currentUser;
      this.initToolbarButtonList();
      this.initPageType();
      this.selectedGrid = this.gridEngineFacade.selectedGridByMasterView;
      this.dataLoadedByGridSelector = this.gridEngineFacade.gridDataLoadedByGridId;
      this.dataLoadingByGridSelector = this.gridEngineFacade.gridDataLoadingByGridId;
      this.gridSortByGridId$
        .pipe(
          filter(() => !this.gridEngineFacade.selectedMasterView() || this.gridEngineFacade.selectedMasterView() === settings.masterViewName()),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((gridSort) => {
          this.gridSort.set([...gridSort]);
        });
      this.initialFilters = toSignal(
        of([]).pipe(
          // Use withLatestFrom to take latest value and prevent infinite loop
          withLatestFrom(this.masterViewService.getInitialFilters()),
          map(([_, initialFilters]) => initialFilters),
          takeUntilDestroyed(this.destroyRef)
        )
      );
      this.toolbarButtons = toSignal(this.masterViewService.getToolbarButtons().pipe(takeUntilDestroyed(this.destroyRef)));
      this.permissions = toSignal(this.masterViewService.getPermissions().pipe(takeUntilDestroyed(this.destroyRef)));
      this.canUpdateBusinessProfile = toSignal(this.masterViewService.canUpdateBusinessProfile().pipe(takeUntilDestroyed(this.destroyRef)));
      this.staticGrid = toSignal(this.masterViewService.getStaticGridDefinition().pipe(takeUntilDestroyed(this.destroyRef)));
      this.gridsConfiguration = toSignal(this.masterViewService.getGridConfiguration().pipe(takeUntilDestroyed(this.destroyRef)));
      this.grids = toSignal(this.masterViewService.getGrids().pipe(takeUntilDestroyed(this.destroyRef)));
      this.favoriteViewsConfiguration = toSignal(this.masterViewService.getFavoriteViewsConfiguration().pipe(takeUntilDestroyed(this.destroyRef)));
      this.currentFavoriteView$ = this.masterViewService.getFavoriteViewsConfiguration().pipe(
        map((conf) => conf?.currentFavoriteView),
        takeUntilDestroyed(this.destroyRef)
      );
      this.currentFavoriteView = toSignal(this.currentFavoriteView$);
      this.dashboardsConfiguration = this.masterViewDashboardService?.getDashboardConfiguration?.();
      this.mapDisplayMode = this.masterViewMapService?.getMapDisplayMode?.();
      this.mapConcept = this.masterViewMapService?.getMapConcept?.();
    });
  }

  private async onBusinessProfileChange() {
    await lastValueFrom(
      this.authFacade.isBusinessProfileChanged$.pipe(
        tap((isBusinessProfileChanged: boolean) => {
          if (isBusinessProfileChanged) {
            this.masterViewService.onBusinessProfileChange(this.masterViewEvent);
          }
        })
      )
    );
  }

  private initFavoriteViewButtonsEffect() {
    effect(
      () => {
        const currentUser = this.currentUser();
        const currentFilters = this.currentFilters();
        const currentFavoriteView: FavoriteView | null = this.currentFavoriteView();
        if (currentUser && currentUser.id) {
          this.toolbarButtonList.update((toolbarButtonList: IotToolbarDefaultButton[]) => {
            toolbarButtonList.forEach((button) => {
              if (button instanceof IotToolbarMenuButton) {
                button.menuOptions.forEach((option: IotToolbarMenuButtonOption) => {
                  if (option.dispatchAction.type === IotToolbarDispatchActionType.CREATE_FAVORITE_VIEW.toString()) {
                    option.disableOption = isEqual(currentFilters.sort(), currentFavoriteView?.filters?.sort());
                  }
                  if (
                    option.dispatchAction.type === IotToolbarDispatchActionType.EDIT_FAVORITE_VIEW.toString() ||
                    option.dispatchAction.type === IotToolbarDispatchActionType.DELETE_FAVORITE_VIEW.toString()
                  ) {
                    if (!this.canUpdateBusinessProfile()) {
                      if (currentFavoriteView?.shared) {
                        option.disableOption = true;
                        return;
                      }
                      option.disableOption = !currentFavoriteView?.filters || currentFavoriteView?.owner !== currentUser.id;
                    } else {
                      option.disableOption = !currentFavoriteView?.filters;
                    }
                  }
                });
              }
            });
            return [...toolbarButtonList];
          });
        }
      },
      { injector: this.injector }
    );
  }

  private initFilterOpenedEffect() {
    effect(
      () => {
        const currentUser = this.currentUser();
        if (currentUser) {
          this.filterEngineOpened.set(get(currentUser, ['preferences', 'filterEngineOpenByDefault'], false));
        }
      },
      { injector: this.injector }
    );
  }

  private initDefaultGridEffect() {
    effect(
      () => {
        const businessProfile = this.businessProfile();
        const grids = this.grids();
        const currentSelectedMasterView = this.gridEngineFacade.selectedMasterView();

        untracked(() => {
          const grid = this.grid();
          const currentFilters = this.currentFilters();
          const myMasterView = this.settings?.masterViewName();

          // Guard: Only load data if the current selected masterview matches this instance's masterview
          // This prevents infinite loops when multiple MasterViews are active simultaneously (e.g., page + dialog)
          // this.selectGridAndLoadData(this.selectedGridId(), myMasterView, currentFilters);
          if (businessProfile && grids?.length && !grid?.data && currentSelectedMasterView === myMasterView) {
            this.selectGridAndLoadData(this.selectedGridId(), myMasterView, currentFilters);
          }
        });
      },
      { injector: this.injector }
    );
  }

  private initSelectedGridByMasterview() {
    effect(
      () => {
        const masterview = this.settings?.masterViewName();
        if (masterview) {
          this.gridEngineFacade.selectCurrentMaterView(masterview);
        }
      },
      { injector: this.injector }
    );
  }

  private initCustomGridOptions() {
    effect(
      () => {
        const masterView = this.settings?.masterViewName?.();
        if (masterView) {
          const options = {
            apiHandlerService: this.settings?.grid?.apiHandlerService
          };
          this.gridEngineFacade.setCustomGridOptions(masterView, options);
        }
      },
      { injector: this.injector }
    );
  }

  private initPageType() {
    this.pageType.set(this.settings.toolbar.pageTypeOptions.defaultPageType());
  }

  private initToolbarButtonList() {
    effect(
      () => {
        const toolbarButtons = this.toolbarButtons();
        if (toolbarButtons?.length > 0) {
          this.toolbarButtonList.set([...toolbarButtons]);
        }
      },
      { injector: this.injector }
    );
  }

  private initGridEffect() {
    effect(
      () => {
        if (this.settings.grid.staticDefinitionType()) {
          const grid = this.staticGrid();
          this.grid.set(grid);
          untracked(() => {
            this.initStaticGridData();
          });
        } else {
          const gridsConfiguration = this.gridsConfiguration();
          this.grid.set(get(gridsConfiguration, 'currentGrid'));
        }
      },
      { injector: this.injector }
    );
  }

  private initGridPaginationEffect() {
    effect(
      () => {
        const grid = this.grid();
        if (!this.settings.grid.staticDefinitionType() && grid?.data && grid.data.response.pagination) {
          this.pagination.set({
            ...grid.data.response.pagination
          } as CommonIndexedPagination);
        }
      },
      { injector: this.injector }
    );
  }

  private initLoadedEffect() {
    effect(
      () => {
        const dataLoadedByGrid = this.dataLoadedByGridSelector();
        this.loaded.set(!!dataLoadedByGrid);
      },
      { injector: this.injector }
    );
    effect(
      () => {
        const dataLoadingByGrid = this.dataLoadingByGridSelector();
        this.loading.set(!!dataLoadingByGrid);
      },
      { injector: this.injector }
    );
  }

  private initTotalEffect() {
    effect(
      () => {
        const staticDefinitionType = this.settings.grid.staticDefinitionType();
        if (!staticDefinitionType) {
          const grid = this.grid();
          if (
            grid?.data?.response?.pagination &&
            grid?.data?.response?.pagination?.limit !== null &&
            grid?.data?.response?.pagination?.limit !== undefined &&
            grid?.data?.response?.pagination?.limit !== -1
          ) {
            this.totalItems.set(get(grid, ['data', 'response', 'pagination', 'total'], 0));
          } else {
            this.totalItems.set(get(grid, ['data', 'response', 'data'], []).length);
          }
        }
      },
      { injector: this.injector }
    );
  }

  private initGridDataEffect() {
    effect(() => this.initStaticGridData(), { injector: this.injector });
  }

  private initStaticGridData() {
    const staticDefinitionType = this.settings.grid.staticDefinitionType();
    if (staticDefinitionType) {
      const platformResponse = this.platformResponse();
      this.grid.update((grid) => {
        if (grid?.data) {
          grid.data.response.data = [...platformResponse.data];
          grid.data.response.pagination = this.pagination();
          this.gridSort.set([...get(grid, ['gridOptions', 'gridSort'], [])]);
        }
        return { ...grid };
      });
    }
  }

  private selectGridAndLoadData(gridId: string, masterview: string, filters: Filter[]) {
    this.gridEngineFacade.selectGridAndLoadData(gridId, masterview, filters);
  }
}
